import React, { useContext, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useTheme } from "react-native-elements";
import { PlayerContext } from "../../backend/PlayerContext";
import {
  getResourceInfo,
  getSortedResourceKeys,
  Resource,
  ResourceData,
} from "../../backend/Resources";
import { calculateIncomePerSecond } from "../../backend/timetick/IncomeOverTime";
import { formatNumber } from "../../utils/FormattingUtils";
import { GameIcon } from "./icons/GameIcon";
import { Text } from "./Text";

const EPSILON = 1e-6;

function ResourceRow(props: {
  resource: Resource;
  current: number;
  cap: number;
  income: number;
  hideIncome: boolean;
}) {
  const resourceInfo = getResourceInfo(props.resource);
  const isCapped = props.current >= props.cap - EPSILON;
  const theme = useTheme();
  const incomeComponent = useMemo(
    () =>
      !props.hideIncome && (
        <View style={styles.income}>
          {props.income != 0 && (
            <Text
              style={props.income < 0 && { color: theme.theme.colors.error }}
              doNotScaleFonts={true}
            >
              {(props.income > 0 ? "+" : "-") +
                formatNumber(Math.abs(props.income), { showDecimals: true })}
              /s
            </Text>
          )}
        </View>
      ),
    [props.income, props.hideIncome],
  );

  const firstComponent = useMemo(
    () => (
      <View style={styles.inlineRow}>
        <GameIcon
          icon={resourceInfo.icon}
          style={styles.icon}
          small
          accessibilityLabel=""
        />
        <Text doNotScaleFonts={true}>{props.resource}: </Text>
      </View>
    ),
    [props.resource, resourceInfo.icon],
  );

  return (
    <View style={styles.row} accessible={true}>
      {firstComponent}
      <View style={styles.inlineRow}>
        <Text
          /*@ts-ignore The cappedResource is not part of react-native-elements*/
          style={isCapped && { color: theme.theme.colors.cappedResource }}
          doNotScaleFonts={true}
        >
          {formatNumber(props.current, { showDecimals: true })}/
          {formatNumber(props.cap)}
        </Text>
        {incomeComponent}
      </View>
    </View>
  );
}

const ResourceRowMemo = React.memo(ResourceRow);

export function ResourceViewer(props: { isVisible?: boolean }) {
  const playerContext = useContext(PlayerContext);
  const resourceKeys = getSortedResourceKeys(playerContext) as Array<Resource>;
  const income = calculateIncomePerSecond(playerContext);
  const hasAnyIncome = Object.keys(income).some(
    (resource) => income[resource as Resource] != 0,
  );
  const rows = useMemo(
    () =>
      resourceKeys.map((resourceName) => {
        const resource = playerContext.resources[resourceName] as ResourceData;
        return (
          <ResourceRowMemo
            resource={resourceName}
            current={resource.current}
            cap={resource.cap}
            income={income?.[resourceName] ?? 0}
            hideIncome={!hasAnyIncome}
            key={resourceName}
          />
        );
      }),
    [props.isVisible !== false ? playerContext : false],
  );
  return <View style={styles.container}>{rows}</View>;
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "stretch",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  inlineRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  icon: {
    marginRight: 2,
  },
  income: {
    width: 65,
    marginLeft: 8,
  },
});
